import { useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Value shape: [{ text: string, done: boolean }]
export function ChecklistFieldRenderer({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const done = items.filter((i) => i.done).length;

  const set = (next) => onChange(next);
  const toggle = (i) => set(items.map((it, j) => (j === i ? { ...it, done: !it.done } : it)));
  const edit = (i, text) => set(items.map((it, j) => (j === i ? { ...it, text } : it)));
  const remove = (i) => set(items.filter((_, j) => j !== i));
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    set([...items, { text: t, done: false }]);
    setDraft("");
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Label className="text-xs">{field.label}</Label>
        {items.length > 0 && (
          <span className="text-[11px] text-muted-foreground">{done}/{items.length}</span>
        )}
      </div>
      {items.length > 0 && (
        <div className="mb-2 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
        </div>
      )}
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 group">
            <button type="button" onClick={() => toggle(i)}
              className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center ${it.done ? "bg-primary border-primary text-primary-foreground" : "border-input"}`}
              data-testid={`checklist-toggle-${i}`}>
              {it.done && <Check className="w-3 h-3" />}
            </button>
            <input
              value={it.text}
              onChange={(e) => edit(i, e.target.value)}
              className={`flex-1 bg-transparent text-sm outline-none border-b border-transparent focus:border-input ${it.done ? "line-through text-muted-foreground" : ""}`}
            />
            <button type="button" onClick={() => remove(i)}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={add}
          placeholder="Add an item…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="checklist-add-input"
        />
      </div>
    </div>
  );
}

// Interactive checklist that PERSISTS on every change (no edit mode).
// Used on the record detail view so phases can be ticked off in place.
export function InlineChecklist({ recordId, fieldKey, initial }) {
  const [items, setItems] = useState(Array.isArray(initial) ? initial : []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const done = items.filter((i) => i.done).length;

  const persist = async (next) => {
    const prev = items;
    setItems(next); setBusy(true);
    try {
      await api.patch(`/records/${recordId}`, { fields: { [fieldKey]: next } });
    } catch {
      setItems(prev);
      toast.error("Couldn't save the checklist");
    } finally { setBusy(false); }
  };
  const toggle = (i) => persist(items.map((it, j) => (j === i ? { ...it, done: !it.done } : it)));
  const remove = (i) => persist(items.filter((_, j) => j !== i));
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    persist([...items, { text: t, done: false }]);
  };

  return (
    <div data-testid="inline-checklist">
      {items.length > 0 && (
        <>
          <div className="text-[11px] text-muted-foreground mb-1">{done}/{items.length} done</div>
          <div className="mb-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
          </div>
        </>
      )}
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 group">
            <button type="button" onClick={() => toggle(i)} disabled={busy}
              className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center ${it.done ? "bg-primary border-primary text-primary-foreground" : "border-input hover:border-primary"}`}
              data-testid={`inline-toggle-${i}`}>
              {it.done && <Check className="w-3 h-3" />}
            </button>
            <span className={`flex-1 text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.text}</span>
            <button type="button" onClick={() => remove(i)}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={add} placeholder="Add an item…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="inline-checklist-add" />
      </div>
    </div>
  );
}

// Compact read-only render for tables/cards.
export function checklistSummary(value) {
  const items = Array.isArray(value) ? value : [];
  if (!items.length) return "—";
  const done = items.filter((i) => i.done).length;
  return `${done}/${items.length}`;
}
